import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode
} from 'react';
import { User as FirebaseUser, type UserCredential } from 'firebase/auth';
import { User as AppUser } from '@/models/UserTypes';
import { useAuthActions } from '@/hooks/useAuthActions';
import { useFirebaseAuthState } from '@/hooks/useFirebaseAuthState';
import { useUserDataState } from '@/hooks/useUserDataState';
import { claimRunOnSignIn } from '@/features/publicDiagnostic/session';

interface AuthContextType {
  // Auth State (useFirebaseAuthState)
  currentUser: FirebaseUser | null;
  /** Combined: auth check OR user-data fetch in flight. Pages that read userData should use this. */
  isLoading: boolean;
  /** Auth check only — for the app shell / route gate, so the whole app doesn't wait on user-data. */
  isAuthLoading: boolean;
  /** User-data fetch only, the other half of `isLoading`. */
  isLoadingUserData: boolean;
  isAuthenticated: boolean;

  // User Data (useUserDataState)
  userData: AppUser | null;
  refreshUserData: () => Promise<void>;
  hasProfile: boolean;
  isSubscribed: boolean;

  // Auth Actions
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (consent?: {
    marketingEmailConsent: boolean;
    transactionalEmailConsent?: boolean;
    termsAcceptedAt: string;
  }, skipUserCreation?: boolean) => Promise<UserCredential>;
  signInWithApple: (consent?: {
    marketingEmailConsent: boolean;
    transactionalEmailConsent?: boolean;
    termsAcceptedAt: string;
  }, skipUserCreation?: boolean) => Promise<UserCredential>;
  /** Redirect-based social sign-in (in-app-browser-safe). Navigates the page away. */
  startSocialRedirect: (provider: 'google' | 'apple') => Promise<void>;
  /** Resolves a pending redirect sign-in on page load; null if none pending. */
  completeSocialRedirect: () => Promise<UserCredential | null>;
  logOut: () => Promise<void>;
  createUser: (email: string, password: string, consent?: {
    marketingEmailConsent: boolean;
    transactionalEmailConsent?: boolean;
    termsAcceptedAt: string;
  }) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  verifyPasswordResetCode: (oobCode: string) => Promise<string>;

  // Dev Mode Support
  isDevMode: boolean;
  isStudentViewActive: boolean;
  activateStudentView: (user?: AppUser) => void;
  deactivateStudentView: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const authState = useFirebaseAuthState();
  const userData = useUserDataState(authState);
  const { actions, devSupport } = useAuthActions();

  const {
    isDevMode,
    isStudentViewActive,
    devUserData,
    mockFirebaseUser,
    activateStudentView,
    deactivateStudentView
  } = devSupport;

  const {
    signIn,
    signInWithGoogle,
    signInWithApple,
    startSocialRedirect,
    completeSocialRedirect,
    logOut,
    createUser,
    resetPassword,
    confirmPasswordReset,
    verifyPasswordResetCode
  } = actions;

  const effectiveUserData = isStudentViewActive ? devUserData : userData.userData;
  const effectiveCurrentUser = (isStudentViewActive && mockFirebaseUser)
    ? mockFirebaseUser
    : authState.currentUser;

  // Student view is an admin, never the learner. A fresh account may be getUserProfile's bare shell (trial fields
  // included) before createTrialUser ran; the signup flows claim those themselves, after provisioning.
  const uid = authState.currentUser?.uid;
  const createdAt = userData.userData?.createdAt;
  const fresh = !!createdAt && Date.now() - Date.parse(createdAt) < 10 * 60_000;
  const claimUid = !isStudentViewActive && uid && userData.userData?.id === uid && !fresh ? uid : undefined;
  useEffect(() => {
    if (claimUid) void claimRunOnSignIn(claimUid);
  }, [claimUid]);
  // Nothing else re-renders when the 10 minutes pass, so re-check once at the boundary.
  const [, setClaimTick] = useState(0);
  useEffect(() => {
    if (!uid || !fresh || !createdAt) return;
    const t = setTimeout(() => setClaimTick((n) => n + 1), Date.parse(createdAt) + 10 * 60_000 - Date.now() + 1000);
    return () => clearTimeout(t);
  }, [uid, fresh, createdAt]);

  const contextValue = useMemo((): AuthContextType => ({
    currentUser: effectiveCurrentUser,
    isLoading: authState.isLoading || userData.isLoadingUserData,
    isAuthLoading: authState.isLoading,
    isLoadingUserData: userData.isLoadingUserData,
    isAuthenticated: authState.isAuthenticated,

    userData: effectiveUserData,
    refreshUserData: userData.refreshUserData,
    hasProfile: userData.hasProfile,
    isSubscribed: userData.isSubscribed,

    signIn,
    signInWithGoogle,
    signInWithApple,
    startSocialRedirect,
    completeSocialRedirect,
    logOut,
    createUser,
    resetPassword,
    confirmPasswordReset,
    verifyPasswordResetCode,

    isDevMode,
    isStudentViewActive,
    activateStudentView,
    deactivateStudentView
  }), [
    effectiveCurrentUser,
    authState.isLoading,
    authState.isAuthenticated,
    effectiveUserData,
    userData.refreshUserData,
    userData.hasProfile,
    userData.isSubscribed,
    userData.isLoadingUserData,
    signIn,
    signInWithGoogle,
    signInWithApple,
    startSocialRedirect,
    completeSocialRedirect,
    logOut,
    createUser,
    resetPassword,
    confirmPasswordReset,
    verifyPasswordResetCode,
    isDevMode,
    isStudentViewActive,
    activateStudentView,
    deactivateStudentView
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useEnhancedAuth = useAuth;

/** useAuth that returns null outside a provider, for public pages that also render bare (tests, prerender). */
export const useOptionalAuth = (): AuthContextType | null => useContext(AuthContext) ?? null;

/** The user-data slice, for callers that only ever wanted the profile. */
export const useUserData = () => {
  const { userData, isLoadingUserData, refreshUserData, hasProfile, isSubscribed } = useAuth();
  return { userData, isLoadingUserData, refreshUserData, hasProfile, isSubscribed };
};

